import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../../../api/notificationsApi';
import type { AppNotification } from '../../../shared/types';

export function useNotifications() {
  const queryClient = useQueryClient();
  const [unreadOnlyFilter, setUnreadOnlyFilter] = useState(false);

  // 1. Unread count query (polls every 30s)
  const unreadCountQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  // 2. Notifications list query
  const notificationsQuery = useQuery<AppNotification[]>({
    queryKey: ['notifications', 'list', { unreadOnly: unreadOnlyFilter }],
    queryFn: () => getNotifications({ unreadOnly: unreadOnlyFilter, page: 0, size: 20 }),
    staleTime: 5000,
  });

  // 3. Mark single as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // 4. Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = unreadCountQuery.data?.count ?? 0;
  const notifications: AppNotification[] = notificationsQuery.data || [];

  return {
    notifications,
    unreadCount,
    isLoading: notificationsQuery.isLoading,
    isError: notificationsQuery.isError,
    unreadOnlyFilter,
    setUnreadOnlyFilter,
    refetchNotifications: notificationsQuery.refetch,
    refetchUnreadCount: unreadCountQuery.refetch,
    markAsRead: markAsReadMutation.mutate,
    isMarkingRead: markAsReadMutation.isPending,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAllRead: markAllAsReadMutation.isPending,
  };
}
